import type { CapturedContext } from './capture';
import type { Locale } from './types';
import { EMAIL_PATTERN } from './ui/dialog';

export interface FeedbackPayload extends CapturedContext {
  token: string;
  reviewerKey: string;
  comment: string;
  screenshot?: string;
  metadata?: Record<string, string>;
  reporterEmail?: string | undefined;
  locale: Locale;
}

export type SubmitResult = { ok: true; id: string } | { ok: false; error: string };

export type ValidateResult =
  | { verdict: 'valid'; defaultReporterEmail?: string }
  | { verdict: 'invalid' }
  | { verdict: 'error' };

export async function submitFeedback(
  endpoint: string,
  payload: FeedbackPayload,
): Promise<SubmitResult> {
  try {
    const res = await fetch(`${endpoint}/api/feedback`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const data = (await res.json().catch(() => ({}))) as { id?: string };
      return { ok: true, id: data.id ?? '' };
    }
    return { ok: false, error: `http_${res.status}` };
  } catch {
    return { ok: false, error: 'network' };
  }
}

/**
 * 'invalid' only on a definitive { valid: false } response — transport/server
 * errors return 'error' so an already-activated widget isn't unmounted by a
 * flaky connection.
 */
export async function validateReviewerKey(
  endpoint: string,
  token: string,
  reviewerKey: string,
): Promise<ValidateResult> {
  try {
    const res = await fetch(`${endpoint}/api/reviewer/validate`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ token, reviewerKey }),
    });
    if (!res.ok) return { verdict: 'error' };
    const data = (await res.json().catch(() => null)) as {
      valid?: boolean;
      defaultReporterEmail?: unknown;
    } | null;
    if (data === null) return { verdict: 'error' };
    if (data.valid !== true) return { verdict: 'invalid' };
    // Wire-boundary validation, same posture as resolveOptions: a malformed
    // server-delivered default is dropped rather than forwarded into the UI.
    const raw =
      typeof data.defaultReporterEmail === 'string' ? data.defaultReporterEmail.trim() : '';
    return raw && EMAIL_PATTERN.test(raw)
      ? { verdict: 'valid', defaultReporterEmail: raw }
      : { verdict: 'valid' };
  } catch {
    return { verdict: 'error' };
  }
}
